import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitListPrice, getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import {
    CartLineModel,
    ProductModel,
    ProductSubscriptionDto,
    VariantTraitModel,
} from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
// import ToasterContext from "@tCentro/MobiusOverrides/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import HistoryContext, { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cenVariantPdpAddToCartClicked from "@tCentro/Handlers/CENPdpVariantAddtoCartHandler";
import CircleInfoIcon from "@tCentro/Icons/CircleInfoIcon";
import CustomCircleInfoIcon from "@tCentro/Icons/CustomCircleInfoIcon";
import ToasterContext from "@tCentro/MobiusOverrides/Toast/ToasterContext";
import ProductAddedToCartMessage from "@tCentro/Overrides/Components/ProductAddedToCartMessage";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import pushAddToCartEvent, { buildItemList } from "@tCentro/Custom/Utilities/dataLayerEvents";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    minErrorValue?: boolean;
    multiErrorValue?: boolean;
    disabledViaList?: boolean;
    itemListOverride?: { itemListId?: string; itemListName?: string };
}

export interface InfoIconTooltipStyles {
    mobileTooltip?: TooltipPresentationProps;
    tooltipContainerStyles?: InjectableCss;
    selectOptionStyles?: InjectableCss;
    hiddenMobileToolTip?: InjectableCss;
    hiddenMobileToolTipNisco?: InjectableCss;
    orderMinContainer?: InjectableCss;
    orderMinAddtoCart?: InjectableCss;
    infoIconStyles?: InjectableCss;
    tooltipButtonContainer?: InjectableCss;
    hiddenlgContainerStyles?: InjectableCss;
    hiddenmdContainerStyles?: InjectableCss;
    fastGuestCheckoutTextSyltes?: InjectableCss;
    ctaCenter?: InjectableCss;
    modalStyle?: ModalPresentationProps;
    promoMsgWrapper?: InjectableCss;
    promoRightTickIcon?: IconPresentationProps;
    promoMsgLinePartOne?: InjectableCss;
    promoMsgLineTwo?: InjectableCss;
    viewCartBtnStyles?: InjectableCss;
    continueBtnStyles?: InjectableCss;
    promoButtonsWrapper?: InjectableCss;
    promoMessageLinesWrapper?: InjectableCss;
}
type PromoDetail = {
    description: string;
    startDate?: string;
    endDate?: string;
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const EXCLUDED_CRUMBS = ["home", "search results", "shop all products", "search"];
const SEARCH_CRUMBS = ["search results", "shop all products", "search"];

const mapStateToProps = (state: ApplicationState & CustomApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const pageType = getCurrentPage(state).type;
    const location = getLocation(state);
    const catalogPage = getCatalogPageStateByPath(state, location.pathname)?.value;

    // ---- Categories -------------------------------------------------------
    const rawCrumbs: { url?: string; text?: string }[] = catalogPage?.breadCrumbs ?? [];
    const productTitle = (props.productContext.product as any)?.productTitle?.trim().toLowerCase();

    // Keep crumbs without a url too (the current page crumb has none),
    // drop Home / search crumbs, and on the PDP drop the product title itself.
    const breadcrumbCategories: string[] = rawCrumbs
        .map(b => b.text?.trim())
        .filter((text): text is string => !!text && !EXCLUDED_CRUMBS.includes(text.toLowerCase()))
        .filter(text => !(pageType === "ProductDetailsPage" && text.toLowerCase() === productTitle));

    const hasSearchResultsBreadcrumb = rawCrumbs.some(b => SEARCH_CRUMBS.includes(b.text?.trim().toLowerCase() ?? ""));

    const productCategoryName =
        (props.productContext.product as any)?.category?.name ??
        (props.productContext.product as any)?.category?.shortDescription ??
        undefined;

    // Full chain, falling back to a single category when there are no breadcrumbs
    const fallbackCategories = [catalogPage?.title ?? productCategoryName].filter(Boolean) as string[];
    const allCategories = breadcrumbCategories.length ? breadcrumbCategories : fallbackCategories;

    // Last category is still used for item_list_name on category pages
    const leafCategoryName = allCategories[allCategories.length - 1];

    const skipCategories =
        pageType === "SearchPage" ||
        pageType === "MyAccountPage" ||
        pageType === "MyListsDetailsPage" ||
        pageType === "BrandDetailsPage" ||
        pageType === "BrandDetailPage" ||
        hasSearchResultsBreadcrumb ||
        (pageType === "ProductListPage" && !!catalogPage?.brandId);

    const itemCategory: string[] | undefined = skipCategories ? undefined : allCategories;

    // ---- Item list --------------------------------------------------------
    let itemList: { itemListId?: string; itemListName?: string } = {};
    if (pageType === "MyListsDetailsPage") {
        // wishlist details: the list itself is the origin
        const wishListId = state.pages.myListDetails.wishListId;
        const wishList = wishListId ? getWishListState(state, wishListId).value : undefined;
        itemList = {
            itemListId: "wishlist_details_page_dynamic_list",
            itemListName: "Wishlist Details Page: Dynamic list",
        };
    } else if (pageType === "ProductDetailsPage") {
        itemList = buildItemList("pdp", "Product Detail Page");
    } else if (pageType === "SearchPage") {
        const query = parseQueryString<{ query?: string }>(location.search.replace("?", "")).query;
        itemList = buildItemList("search", query ?? "Search Results");
    } else if (pageType === "BrandDetailsPage" || pageType === "BrandDetailPage") {
        itemList = buildItemList("brand", catalogPage?.title);
    } else if (pageType === "CategoryDetailsPage" || pageType === "CategoryPage") {
        itemList = buildItemList("category", leafCategoryName);
    } else if (pageType === "ProductComparePage") {
        itemList = buildItemList("compare", " Products Page");
    } else {
        // any other listing page: search query takes priority, then brand/category id, else generic PLP
        const query = parseQueryString<{ query?: string }>(location.search.replace("?", "")).query;

        if (query) itemList = buildItemList("search", query);
        else if (catalogPage?.brandId) itemList = buildItemList("brand", catalogPage?.title);
        else if (catalogPage?.categoryId) itemList = buildItemList("category", leafCategoryName ?? catalogPage.title);
        else itemList = buildItemList("plp", "Product List Page");
    }

    // ---- Cart / promotions ------------------------------------------------
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    const { cartId } = state.pages.onePageCheckout;
    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);
    const promotions = promotionsDataView.value;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            !!props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        isProductDetailPage: getCurrentPage(state).type === "ProductDetailsPage",
        isProductListPage: getCurrentPage(state).type,
        variantSelection: state.pages.productDetails.variantSelection,
        cenVariantPdpAddToCart: state.t_components.cenVariantPdpAddToCartIsClicked.variantPdpAddToCartClicked,
        currentUserIsGuest: state.context.session?.isGuest,
        isAuthenticated: state.context.session?.isAuthenticated,
        isMinErrorObj: state.t_components.isMinError,
        isMultiErrorObj: state.t_components.isMultiError,
        siteName: state.context.website.name,
        cart: getCurrentCartState(state).value,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        currencyCode: state.context.session?.currency?.currencyCode,
        promotions,
        promotionsDataView,
        itemListId: itemList.itemListId,
        itemListName: itemList.itemListName,
        itemCategory,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    cenVariantPdpAddToCartClicked,
};

export const productAddToCartButtonStyles: ButtonPresentationProps & InfoIconTooltipStyles = {
    size: 36,
    shape: "rectangle",
    css: css`
        position: relative !important;
    `,
    typographyProps: {
        css: css`
            font-size: 14px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            padding-top: 3px;
            line-height: 16px;
        `,
    },
    tooltipButtonContainer: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        align-items: center;
                    `,
                    css`
                        flex-direction: column-reverse;
                        align-items: center;
                    `,
                    css`
                        flex-direction: column-reverse;
                        align-items: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    promoButtonsWrapper: {
        css: css`
            display: flex;
            gap: 16px;
            padding-top: 20px;
        `,
    },
    promoMessageLinesWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
        `,
    },

    infoIconStyles: {
        css: css`
            position: relative;
            left: -7px;
        `,
    },
    orderMinContainer: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    orderMinAddtoCart: {
        css: css`
            font-size: 14px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px;
        `,
    },
    hiddenMobileToolTip: {
        css: css`
            display: inline;
            position: absolute;
            padding-left: 2px;

            @media all and (min-width: 768px) and (max-width: 1024px) {
                left: 0%;
                top: 17%;
                padding-left: 8px;
            }
            @media all and (min-width: 820px) and (max-width: 1180px) {
                left: 0%;
                bottom: 10%;
                padding-left: 7px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 6px;
                    `,
                    css`
                        left: 26%;
                        bottom: 10%;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
            @media (min-width: 992px) and (max-width: 1024px) {
                display: block !important;
            }
            @media (min-width: 1025px) {
                display: none !important;
            }
        `,
    },
    hiddenlgContainerStyles: {
        css: css`
            @media (min-width: 992px) and (max-width: 1024px) {
                display: none !important;
            }
            @media (min-width: 1025px) {
                display: block !important;
            }
        `,
    },
    hiddenMobileToolTipNisco: {
        css: css`
            display: inline;
            position: absolute;
            padding-left: 2px;

            @media all and (min-width: 768px) and (max-width: 1024px) {
                left: 13%;
                top: 33%;
                padding-left: 8px;
            }
            @media all and (min-width: 820px) and (max-width: 1180px) {
                left: 13%;
                bottom: 10%;
                padding-left: 7px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 10px;
                        padding-top: 6px;
                    `,
                    css`
                        left: 26%;
                        bottom: 10%;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
            @media (min-width: 992px) and (max-width: 1024px) {
                display: block !important;
            }
            @media (min-width: 1025px) {
                display: none !important;
            }
        `,
    },
    hiddenmdContainerStyles: {
        css: css`
            width: 100%;
            @media (min-width: 992px) and (max-width: 1024px) {
                display: block !important;
            }
            @media (min-width: 1025px) {
                display: none !important;
            }
        `,
    },
    mobileTooltip: {
        iconProps: {
            src: CircleInfoIcon,
        },
        cssOverrides: {
            tooltipBody: css`
                position: relative;
                z-index: 999;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            right: 40px;
                        `,
                        css`
                            right: 40px;
                        `,
                        css`
                            right: 40px;
                        `,
                        css`
                            @media (min-width: 992px) and (max-width: 1024px) {
                                right: 80px;
                            }
                        `,
                        null,
                    ])}
            `,
        },
    },
    selectOptionStyles: {
        css: css`
            font-family: Poppins;
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            text-align: left;
        `,
    },
    tooltipContainerStyles: {
        css: css`
            background-color: #262525;
            border-radius: 4px;
            width: max-content;
            box-shadow: 0px 4px 8px 0px rgba(38, 37, 37, 0.24);
            background-color: #262525;
            border-radius: 4px;
            box-shadow: 0px 4px 8px 0px rgba(38, 37, 37, 0.24);
            position: absolute;
            top: -36px;
            right: -5px;
            z-index: 999;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px;
            padding: 10px;
            color: white;
            &:after {
                content: "";
                border-left: 0px solid #262525;
                border-right: 4px solid #262525;
                position: absolute;
                z-index: 999;
                top: 29px;
                left: 50%;
                width: 0px;
                height: 16px;
                width: 0px;
                height: 0px;
                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
                border-top: 20px solid #262525;
            }
        `,
    },
    fastGuestCheckoutTextSyltes: {
        css: css`
            font-size: 12px !important;
            line-height: 12px !important;
        `,
    },
    ctaCenter: {
        css: css`
            text-align: center;
        `,
    },
    modalStyle: {
        cssOverrides: {
            modalTitle: css`
                padding: 40px;
                padding-bottom: 0px;
                border-bottom: 0px;
                h4 {
                    display: none;
                }

                button[data-test-selector^="modalCloseButton"] {
                    position: absolute;
                    right: 16px;
                    top: 16px;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 20px;
                            padding-bottom: 0px;
                        `,
                        css`
                            padding: 20px;
                            padding-bottom: 0px;
                        `,
                        css``,
                        css``,
                        null,
                    ])}
            `,
            modalContent: css`
                padding: 40px;
                padding-top: 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 20px;
                        `,
                        css`
                            padding: 20px;
                        `,
                        css``,
                        css``,
                        null,
                    ])}
            `,
        },
    },
    promoMsgWrapper: {
        css: css`
            display: flex;
            gap: 4px;
        `,
    },
    promoRightTickIcon: {
        css: css`
            width: 40px;
            height: 40px;
            color: #266b40;
            svg {
                width: 40px;
                height: 40px;
            }
        `,
    },
    promoMsgLinePartOne: {
        css: css`
            font-size: 32px;
            font-weight: 700;
            line-height: 40px;
            color: ${COLORS.black};
        `,
    },
    promoMsgLineTwo: {
        css: css`
            font-weight: 400;
            font-family: Poppins;
            font-size: 12px;
            line-height: 16px;
            color: #636161;
        `,
    },
    viewCartBtnStyles: {
        // variant: "primary",
        css: css`
            background-color: ${getColor("secondary")} !important;
            border-radius: 24px !important;
            min-height: unset !important;
            width: 100%;
            justify-content: center;
            max-width: fit-content;
            padding: 12px 24px !important;
            > span {
                font-size: 18px;
                font-style: normal;
                font-weight: 600;
                line-height: 24px;
                color: ${getColor("secondary.contrast")} !important;
            }
        `,
    },
    continueBtnStyles: {
        css: css`
            background-color: white !important;
            border: 2px solid ${getColor("secondary")} !important;
            border-radius: 24px !important;
            min-height: unset !important;
            width: 100%;
            padding: 10px !important;
            justify-content: center;
            max-width: fit-content;
            padding: 12px 24px !important;
            > span {
                font-size: 18px;
                color: ${getColor("secondary")} !important;
                font-style: normal;
                font-weight: 600;
                line-height: 24px;
            }
        `,
    },
};
const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired, isVariantParent },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory },
    },
    isProductDetailPage,
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    productContext,
    variantSelection,
    cenVariantPdpAddToCart,
    isProductListPage,
    cenVariantPdpAddToCartClicked,
    currentUserIsGuest,
    minErrorValue,
    multiErrorValue,
    isMinErrorObj,
    isMultiErrorObj,
    disabledViaList,
    siteName,
    isAuthenticated,
    cartUrl,
    currencyCode,
    promotions,
    promotionsDataView,
    itemListId,
    itemListName,
    itemListOverride,
    itemCategory,
    ...otherProps
}: Props) => {
    const history = useHistory();
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const [showTooltip, setShowTooltip] = useState(false);
    const [showUnselectedVariants, setShowUnselectedVariants] = useState(false);
    const [qtyBasedAddToCartDisableMin, setQtyBasedAddToCartDisableMin] = useState(false);
    const [qtyBasedAddToCartDisableMulti, setQtyBasedAddToCartDisableMulti] = useState(false);
    const [showPromoModal, setShowPromoModal] = useState(false);
    const [promoDetails, setPromoDetails] = useState<PromoDetail[]>([]);

    useEffect(() => {
        setQtyBasedAddToCartDisableMin(false);
        setQtyBasedAddToCartDisableMulti(false);

        const multipleSaleQty = productContext.product?.detail?.multipleSaleQty;

        if (
            multipleSaleQty &&
            multipleSaleQty > 0 &&
            productContext.product?.detail?.roundingRule === "Multiple Only"
        ) {
            if (qtyOrdered < multipleSaleQty) {
                setQtyBasedAddToCartDisableMin(true);
            } else if (qtyOrdered > multipleSaleQty && qtyOrdered % multipleSaleQty !== 0) {
                setQtyBasedAddToCartDisableMulti(true);
            }
        }
    }, [qtyOrdered]);

    useEffect(() => {
        if (
            productContext?.product?.detail?.multipleSaleQty &&
            productContext?.product?.detail?.multipleSaleQty > 0 &&
            productContext?.product?.detail?.roundingRule === "Multiple Only" &&
            qtyOrdered === 1
        ) {
            if (productContext?.onQtyOrderedChanged) {
                productContext.onQtyOrderedChanged(productContext?.product?.detail?.multipleSaleQty);
            }
        }
    }, [productContext?.product?.detail?.multipleSaleQty]);

    const handleMouseEnter = () => {
        orderMinimumTotal !== undefined && setShowTooltip(true);
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    if (!hasEnoughInventory && !failedToLoadInventory) {
        return null;
    }

    const handleVariantHandler = (event: React.MouseEvent) => {
        if (isProductListPage) {
            history.push(productContext?.product?.canonicalUrl);
        }
        cenVariantPdpAddToCartClicked({ variantPdpAddToCartClicked: true });

        const errorSummaryContainer = document.getElementsByClassName("errorSummaryContainer")[1];
        if (errorSummaryContainer && cenVariantPdpAddToCart) {
            errorSummaryContainer.scrollIntoView({ behavior: "smooth" });
        }
    };
    const formatTimeWithBusinessZone = (dateString: string) => {
        const date = new Date(dateString);
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const time = date.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });

        const offsetMinutes = -date.getTimezoneOffset();

        if (userTimeZone === "Asia/Kolkata" || offsetMinutes === 330) {
            return `${time} IST`;
        }

        const zone = date.toLocaleTimeString(undefined, { timeZoneName: "short" }).split(" ").pop();

        return `${time} ${zone}`;
    };
    const getRemainingTime = (endDate: string) => {
        const now = new Date();
        const end = new Date(endDate);

        const diffMs = end.getTime() - now.getTime();
        if (diffMs <= 0) {
            return null;
        }

        const totalMinutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        const time = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

        return {
            time,
            label: "remaining",
        };
    };
    const addToCartClickHandler = async () => {
        onAddToCartClick?.();
        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }
        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;
        const extractPromoDetails = (cartline: CartLineModel): PromoDetail[] => {
            const raw = cartline?.properties?.promoDetails;

            if (typeof raw !== "string") {
                return [];
            }

            try {
                const parsed = JSON.parse(raw);

                if (!Array.isArray(parsed)) {
                    return [];
                }

                return parsed
                    .filter(item => item?.description)
                    .map(item => ({
                        description: item.description,
                        startDate: item.startDate,
                        endDate: item.endDate,
                    }));
            } catch {
                return [];
            }
        };

        const promos = extractPromoDetails(cartline);
        setPromoDetails(promos);

        const addedQty = cartline?.qtyOrdered ?? qtyOrdered;
        const unitNetPrice = pricing?.actualPrice ?? (pricing ? getUnitNetPrice(pricing, addedQty).price : 0);
        const unitRegularPrice = pricing?.extendedRegularPrice ?? unitNetPrice;
        const discountPrice = unitRegularPrice > unitNetPrice ? unitRegularPrice - unitNetPrice : 0;
        const discountPriceFixed = discountPrice.toFixed(2);
        const linePromotion = promotions?.find(p => p.orderLineId === cartline?.id);
        const coupon = linePromotion?.name ?? promos.map(p => p.description).join(", ");

        pushAddToCartEvent({
            itemId: productContext.product.manufacturerItem,
            itemName: productContext.product.productTitle,
            price: unitNetPrice,
            quantity: addedQty,
            brand: productContext.product.brand?.name,
            itemCategory,
            currency: currencyCode,
            itemListId: itemListOverride?.itemListId ?? itemListId,
            itemListName: itemListOverride?.itemListName ?? itemListName,
        });

        const toastTimeout = cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout;

        if (showAddToCartConfirmationDialog) {
            const cartLines = cartline ? [cartline] : null;

            const hasPromoDetails = promos.length > 0;

            toasterContext.addToast({
                body: (
                    <ProductAddedToCartMessage
                        isQtyAdjusted={cartline.isQtyAdjusted}
                        brandName={cartline?.brand?.name}
                        multipleProducts={false}
                        cartLines={cartLines}
                        toasterDisplayVal={toasterContext.toastDisplayNo()}
                        onToastDismiss={() => {
                            if (hasPromoDetails) {
                                setShowPromoModal(true);
                            }
                        }}
                    />
                ),
                messageType: "success",
                timeoutLength: toastTimeout,
                cssOverrides: {
                    toast: css`
                        width: 522px !important;
                        max-height: 578px;
                        overflow-x: hidden;
                        overflow-y: auto;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    max-height: unset;
                                `,
                                css`
                                    max-height: unset;
                                `,
                                css`
                                    max-height: unset;
                                `,
                                null,
                                null,
                            ])}
                        border: none !important;
                        background: ${COLORS.white} !important;
                        box-shadow: 0px 12px 24px rgba(38, 37, 37, 0.24) !important;
                        padding: 45px 64px;
                        display: block;
                        button {
                            background: none;
                            border: none;
                            outline: none !important;
                            > span {
                                color: ${getProp("theme.typography.p.color")};
                            }
                        }
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    padding: 40px;
                                `,
                                null,
                                null,
                                null,
                                null,
                            ])}
                    `,
                    toastBody: css`
                        margin: 0 !important;
                        > span {
                            &:first-child {
                                display: none;
                            }
                            &:last-child {
                                flex-basis: 100% !important;
                            }
                        }
                    `,
                },
            });
        }
    };

    const orderMinimumTotal = productContext?.product?.properties?.orderMinimumTotal;
    const promoModalCloseHandler = () => setShowPromoModal(false);

    return (
        <StyledWrapper {...styles.tooltipButtonContainer} style={{ position: "relative" }}>
            <Button
                {...styles}
                onClick={!productContext?.product?.isVariantParent ? addToCartClickHandler : handleVariantHandler}
                disabled={
                    qtyOrdered <= 0 ||
                    isAddToCartButtonDisabled ||
                    qtyBasedAddToCartDisableMin ||
                    qtyBasedAddToCartDisableMulti ||
                    disabledViaList
                }
                {...otherProps}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {orderMinimumTotal !== undefined ? (
                    <>
                        <Hidden below="lg" {...styles.hiddenlgContainerStyles}>
                            {isProductDetailPage || !productContext?.product?.isVariantParent ? (
                                <StyledWrapper {...styles.orderMinContainer}>
                                    <Typography as="span" {...styles.infoIconStyles}>
                                        <CustomCircleInfoIcon />
                                    </Typography>

                                    <Typography as="span" {...styles.orderMinAddtoCart}>
                                        {labelOverride ?? translate("Add to Cart")}
                                        <Hidden below="lg">
                                            {showTooltip && orderMinimumTotal !== undefined && (
                                                <StyledWrapper {...styles.tooltipContainerStyles}>
                                                    <Typography as="span">
                                                        {siteMessage("orderMinimumMsgPLP", `${orderMinimumTotal}`)}
                                                    </Typography>
                                                </StyledWrapper>
                                            )}
                                        </Hidden>
                                    </Typography>
                                </StyledWrapper>
                            ) : (
                                !isProductDetailPage && (
                                    <Typography as="span" {...styles.selectOptionStyles}>
                                        {translate("Select Options")}
                                    </Typography>
                                )
                            )}
                        </Hidden>
                        <Hidden above="md" {...styles.hiddenmdContainerStyles}>
                            {isProductDetailPage || !productContext?.product?.isVariantParent ? (
                                <Typography as="div" {...styles.ctaCenter}>
                                    {labelOverride ?? translate("Add to Cart")}
                                </Typography>
                            ) : (
                                <Typography as="span" {...styles.selectOptionStyles}>
                                    {translate("Select Options")}
                                </Typography>
                            )}
                        </Hidden>
                    </>
                ) : !productContext?.product?.isVariantParent ? (
                    <>
                        <Typography as="p">{labelOverride ?? translate("Add to Cart")}</Typography>
                        {isProductDetailPage && !currentUserIsGuest && !isAuthenticated && (
                            <Typography {...styles.fastGuestCheckoutTextSyltes} as="p">
                                {translate("Fast guest checkout")}
                            </Typography>
                        )}
                    </>
                ) : !isProductDetailPage ? (
                    <Typography as="span" {...styles.selectOptionStyles}>
                        {translate("Select Options")}
                    </Typography>
                ) : (
                    <Typography
                        as="span"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            lineHeight: "24px",
                            padding: 10,
                            gap: 2,
                            fontSize: "16px",
                        }}
                    >
                        {productContext?.product?.isVariantParent ? (
                            <>
                                <span>{translate("Configure to Add to Cart")}</span>
                            </>
                        ) : (
                            <span>{labelOverride ?? translate("Add to Cart")}</span>
                        )}
                    </Typography>
                )}
            </Button>

            {orderMinimumTotal !== undefined && !productContext?.product?.isVariantParent && (
                <Hidden
                    above="md"
                    {...(siteName === "Nisco" ? styles.hiddenMobileToolTipNisco : styles.hiddenMobileToolTip)}
                >
                    <Tooltip
                        text={siteMessage("orderMinimumMsgPLP", `${orderMinimumTotal}`) as string}
                        {...styles.mobileTooltip}
                    />
                </Hidden>
            )}
            {showPromoModal && (
                <Modal
                    isOpen={showPromoModal}
                    handleClose={promoModalCloseHandler}
                    {...styles.modalStyle}
                    headline="Special Promotion Applied 🎉"
                >
                    {promoDetails.map((promo, index) => {
                        const isLast = index === promoDetails.length - 1;
                        const remaining = promo.endDate && getRemainingTime(promo.endDate);

                        return (
                            <StyledWrapper
                                key={promo.description ?? index}
                                {...styles.promoMsgWrapper}
                                style={{
                                    marginBottom: isLast ? 0 : "8px",
                                    paddingBottom: isLast ? "20px" : "0px",
                                }}
                            >
                                <Icon {...styles.promoRightTickIcon} src={Check} />

                                <StyledWrapper {...styles.promoMessageLinesWrapper}>
                                    <StyledWrapper {...styles.promoMsgLinePartOne}>
                                        {siteMessage("promoPopUpDiscountText", `${promo.description}`)}
                                    </StyledWrapper>

                                    {promo.endDate && (
                                        <StyledWrapper {...styles.promoMsgLineTwo}>
                                            {siteMessage(
                                                "promoValidTillPopUpMessage",
                                                formatTimeWithBusinessZone(promo.endDate),
                                            )}

                                            {remaining && (
                                                <Typography style={{ marginLeft: "4px" }}>
                                                    (
                                                    <Typography
                                                        css={css`
                                                            color: ${getColor("primary")};
                                                            font-weight: 600;
                                                        `}
                                                    >
                                                        {remaining.time}
                                                    </Typography>{" "}
                                                    <span>{remaining.label}</span>)
                                                </Typography>
                                            )}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        );
                    })}

                    <StyledWrapper {...styles.promoButtonsWrapper}>
                        <Link href={cartUrl} {...styles.viewCartBtnStyles} target="_parent">
                            {translate("View Cart")}
                        </Link>
                        <Link
                            href="#"
                            {...styles.continueBtnStyles}
                            onClick={() => setShowPromoModal(false)}
                            type="button"
                        >
                            {translate("Continue Shopping")}
                        </Link>
                    </StyledWrapper>
                </Modal>
            )}
        </StyledWrapper>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
